import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  HStack,
  Icon,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdCreditCard, MdAccountBalance } from 'react-icons/md';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import API from 'api/api';

// Stripe is loaded lazily — only when this component mounts
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

// ── Card payment form ──────────────────────────────────────────────────────────

function CardForm({ listingId, amount, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const [clientSecret, setClientSecret] = useState(null);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [paying, setPaying] = useState(false);
  const [intentError, setIntentError] = useState(null);

  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const inputBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  const cardInputBg = useColorModeValue('white', 'navy.800');

  const createIntent = useCallback(async () => {
    setLoadingIntent(true);
    setIntentError(null);
    try {
      const res = await API.post('/payments/create-intent', { listingId });
      setClientSecret(res.data.clientSecret);
    } catch (err) {
      setIntentError(err?.response?.data?.message || 'Failed to start payment session.');
    } finally {
      setLoadingIntent(false);
    }
  }, [listingId]);

  useEffect(() => {
    createIntent();
  }, [createIntent]);

  const handlePay = async () => {
    if (!stripe || !elements || !clientSecret) return;
    setPaying(true);
    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (error) {
        toast({ title: error.message, status: 'error', duration: 5000, isClosable: true });
      } else if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing') {
        // Before calling onSuccess, ensure backend has synced the status
        // This is especially important for Revolut or local payment methods 
        // that may stay in 'processing' for a moment.
        try {
          await API.post(`/listings/${listingId}/refresh-stripe-payment`);
        } catch (syncErr) {
          console.error('[Payment sync error]', syncErr);
          // We continue anyway since confirmCardPayment was successful
        }
        
        toast({ 
          title: paymentIntent.status === 'succeeded' ? 'Payment successful!' : 'Payment processing...', 
          status: 'success', 
          duration: 4000, 
          isClosable: true 
        });
        onSuccess();
      }
    } finally {
      setPaying(false);
    }
  };

  if (loadingIntent) {
    return (
      <HStack justify="center" py={6}>
        <Spinner size="sm" color="blue.400" />
        <Text fontSize="sm" color={mutedColor}>Preparing payment session…</Text>
      </HStack>
    );
  }

  if (intentError) {
    return (
      <VStack spacing={3} py={4}>
        <Text color="red.500" fontSize="sm">{intentError}</Text>
        <Button size="sm" variant="outline" colorScheme="blue" onClick={createIntent}>
          Retry
        </Button>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={5}>
      <Box
        border="1px solid"
        borderColor={inputBorder}
        borderRadius="xl"
        p={4}
        bg={cardInputBg}
      >
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#2D3748',
                '::placeholder': { color: '#A0AEC0' },
              },
            },
            hidePostalCode: true,
          }}
        />
      </Box>

      <Button
        colorScheme="blue"
        borderRadius="xl"
        fontWeight="700"
        size="lg"
        isLoading={paying}
        loadingText="Processing…"
        onClick={handlePay}
        isDisabled={!stripe || !clientSecret}
      >
        Pay €{amount?.toFixed(2)}
      </Button>

      <Text fontSize="xs" color={mutedColor} textAlign="center">
        Secured by Stripe. Your card details are never stored on our servers.
      </Text>
    </VStack>
  );
}

// ── SEPA bank transfer details ─────────────────────────────────────────────────

function SepaDetails({ listingHumanId, amount, listingId }) {
  const toast = useToast();
  const [bankDetails, setBankDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const labelColor = useColorModeValue('gray.600', 'gray.300');
  const titleColor = useColorModeValue('navy.800', 'white');
  const highlightBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const highlightBorder = useColorModeValue('blue.100', 'whiteAlpha.200');

  useEffect(() => {
    API.get('/payments/bank-details')
      .then((res) => setBankDetails(res.data))
      .catch(() => setBankDetails(null))
      .finally(() => setLoading(false));
  }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({ title: `${label} copied`, status: 'info', duration: 1500 });
    });
  };

  const reference = `LOGI-${listingHumanId || 'REF'}`;

  if (loading) {
    return (
      <HStack justify="center" py={6}>
        <Spinner size="sm" color="blue.400" />
      </HStack>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Box bg={highlightBg} border="1px solid" borderColor={highlightBorder} borderRadius="xl" p={4}>
        <Text fontSize="sm" fontWeight="700" color="blue.700" mb={3}>
          Transfer exactly this amount:
        </Text>
        <Text fontSize="2xl" fontWeight="900" color="blue.600">
          €{amount?.toFixed(2)} EUR
        </Text>
      </Box>

      <VStack align="stretch" spacing={3} divider={<Divider />}>
        {[
          { label: 'Bank name', value: bankDetails?.bankName },
          { label: 'IBAN', value: bankDetails?.iban },
          { label: 'BIC / SWIFT', value: bankDetails?.bic },
          { label: 'Reference (required)', value: reference, highlight: true },
        ].map(({ label, value, highlight }) => (
          <HStack key={label} justify="space-between" align="flex-start">
            <VStack align="flex-start" spacing={0}>
              <Text fontSize="xs" color={labelColor} fontWeight="600">
                {label}
              </Text>
              <Text
                fontWeight="700"
                color={highlight ? 'orange.600' : titleColor}
                fontSize={highlight ? 'md' : 'sm'}
              >
                {value || '—'}
              </Text>
            </VStack>
            {value && (
              <Button size="xs" variant="ghost" colorScheme="blue" onClick={() => copy(value, label)}>
                Copy
              </Button>
            )}
          </HStack>
        ))}
      </VStack>

      <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="xl" p={4}>
        <Text fontSize="sm" color="orange.700" fontWeight="700" mb={1}>
          Important — use the exact reference
        </Text>
        <Text fontSize="sm" color="orange.600">
          You must include <strong>{reference}</strong> in the payment description so
          we can match your transfer. Once received, our team will confirm your payment
          and release your transport.
        </Text>
      </Box>

      {listingId && (
        <Button
          variant="outline"
          colorScheme="blue"
          size="sm"
          borderRadius="xl"
          mt={2}
          onClick={async () => {
            try {
              const res = await API.get(`/listings/${listingId}/proforma-invoice`);
              if (res.data?.url) window.open(res.data.url, '_blank');
            } catch {
              // silent fail
            }
          }}
        >
          Download Proforma Invoice
        </Button>
      )}

      <Text fontSize="xs" color={mutedColor} textAlign="center">
        SEPA transfers typically arrive within 1–2 business days.
        Our team confirms payment manually during business hours.
      </Text>
    </VStack>
  );
}

// ── Public component ───────────────────────────────────────────────────────────

export default function PaymentPanel({ listing, acceptedQuote, onPaymentComplete }) {
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const titleColor = useColorModeValue('navy.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const listingId = listing?._id || listing?.id;
  const listingHumanId = listing?.humanId;

  // Gross price from accepted quote
  const grossPrice =
    acceptedQuote?.grossPrice ??
    (acceptedQuote?.netPrice && acceptedQuote?.vatRate != null
      ? acceptedQuote.netPrice * (1 + acceptedQuote.vatRate / 100)
      : null);

  if (!stripePromise) {
    // Stripe key not configured — only show bank transfer
    return (
      <Box p={5} bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl">
        <Text fontSize="md" fontWeight="800" mb={1} color={titleColor}>Pay by Bank Transfer</Text>
        <Text fontSize="sm" color={mutedColor} mb={4}>
          Amount: <strong>€{grossPrice?.toFixed(2) ?? '—'}</strong>
        </Text>
        <SepaDetails listingHumanId={listingHumanId} amount={grossPrice} listingId={listingId} />
      </Box>
    );
  }

  return (
    <Box p={5} bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="2xl">
      <Text fontSize="md" fontWeight="800" mb={1} color={titleColor}>Complete Payment</Text>
      <Text fontSize="sm" color={mutedColor} mb={5}>
        Amount due: <strong>€{grossPrice?.toFixed(2) ?? '—'} EUR</strong>
      </Text>

      <Tabs variant="soft-rounded" colorScheme="blue" size="sm">
        <TabList mb={5}>
          <Tab fontWeight="700">
            <Icon as={MdCreditCard} mr={2} />
            Card
          </Tab>
          <Tab fontWeight="700">
            <Icon as={MdAccountBalance} mr={2} />
            Bank Transfer (SEPA)
          </Tab>
        </TabList>

        <TabPanels>
          {/* Card payment */}
          <TabPanel p={0}>
            <Elements stripe={stripePromise}>
              <CardForm
                listingId={listingId}
                amount={grossPrice}
                onSuccess={onPaymentComplete}
              />
            </Elements>
          </TabPanel>

          {/* SEPA bank transfer */}
          <TabPanel p={0}>
            <SepaDetails listingHumanId={listingHumanId} amount={grossPrice} listingId={listingId} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
